import { useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useI18n } from '@/hooks/useI18n';
import { isPortalSection, routes } from '@/lib/routes';
import { AboutSection } from './components/AboutSection';
import { CompetitionsSection } from './components/CompetitionsSection';
import { ContactsSection } from './components/ContactsSection';
import { EducationSection } from './components/EducationSection';
import { InstructionSection } from './components/InstructionSection';
import { MaterialsSection } from './components/MaterialsSection';
import { NewsSection } from './components/NewsSection';
import { PortalHome } from './components/PortalHome';
import { VacanciesSection } from './components/VacanciesSection';
import type { PortalTab } from './types';

const SECTION_TITLES: Record<PortalTab, string> = {
  home: 'Публичная лента',
  news: 'Новости',
  competitions: 'Конкурсы',
  materials: 'Материалы',
  education: 'Образование',
  vacancies: 'Вакансии',
  about: 'О нас',
  instructions: 'Инструкция',
  contacts: 'Контакты',
};

export const PortalPage = () => {
  const { t } = useI18n();
  const { section } = useParams<{ section: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [hash, setHash] = useState(() => location.hash.replace(/^#/, ''));

  useEffect(() => {
    setHash(location.hash.replace(/^#/, ''));
  }, [location.hash, location.pathname]);

  useEffect(() => {
    if (!hash) return;
    const target = document.getElementById(`portal-${hash}`);
    if (target) {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [hash, section]);

  if (!section || !isPortalSection(section)) {
    return <Navigate to={routes.portalSection('about')} replace />;
  }

  const activeTab = section;

  const handleTabChange = (tab: PortalTab) => {
    navigate(routes.portalSection(tab));
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'home':
        return <PortalHome onTabChange={handleTabChange} />;
      case 'news':
        return <NewsSection filterKey={hash} />;
      case 'competitions':
        return <CompetitionsSection filterKey={hash} />;
      case 'materials':
        return <MaterialsSection filterKey={hash} />;
      case 'education':
        return <EducationSection filterKey={hash} />;
      case 'vacancies':
        return <VacanciesSection filterKey={hash} />;
      case 'about':
        return <AboutSection focusId={hash} />;
      case 'instructions':
        return <InstructionSection focusId={hash} />;
      case 'contacts':
        return <ContactsSection focusId={hash} />;
      default:
        return <PortalHome onTabChange={handleTabChange} />;
    }
  };


  return (
    <div className="public-body portal-shell" data-portal-section={activeTab}>
      <div className="portal-shell-head">
        <div>
          <div className="portal-shell-kicker">Публичный портал</div>
          <h1>{SECTION_TITLES[activeTab]}</h1>
        </div>
        {!user ? (
          <button type="button" className="portal-shell-login" onClick={() => navigate(routes.login)}>
            {t('topbar.signIn')}
          </button>
        ) : null}
      </div>
      <div className="portal-shell-body">{renderContent()}</div>
    </div>
  );
};

export default PortalPage;
